/**
 * Les Moves débloqués par compte (table `user_moves`, migration 0750) : le compte lit et ajoute les siens,
 * l'admin lit et retire ceux de n'importe quel compte. Le catalogue vit dans le code
 * (`features/moves/move-catalog.ts`) ; la table ne porte que les ids acquis.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// table postérieure aux types générés
const db = supabase as unknown as SupabaseClient;

export interface UserMoveRow {
  move_id: string;
  unlocked_at: string;
  /** les HORA globales gagnées au déblocage, posées par le serveur (`move_catalog`, migration 0751) */
  hora: number;
}

/** Les HORA globales gagnées en débloquant des Moves. PUR. */
export function movesHoraTotal(rows: readonly Pick<UserMoveRow, 'hora'>[] | undefined): number {
  return (rows ?? []).reduce((sum, r) => sum + (Number.isFinite(r.hora) ? r.hora : 0), 0);
}

export const userMovesKey = (userId: string | null | undefined) => ['user-moves', userId ?? ''];

export async function fetchUserMoves(userId: string): Promise<UserMoveRow[]> {
  const { data, error } = await db
    .from('user_moves')
    .select('move_id, unlocked_at, hora')
    .eq('user_id', userId)
    .order('unlocked_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as UserMoveRow[];
}

/** Les Moves d'un compte. `enabled` faux : pas de compte (visiteur, session anonyme). */
export function useUserMoves(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: userMovesKey(userId),
    queryFn: () => fetchUserMoves(userId!),
    enabled: enabled && !!userId,
    staleTime: 60_000,
  });
}

/** Le compte débloque un Move. Un doublon est ignoré (déjà acquis). */
export async function addUserMove(userId: string, moveId: string): Promise<void> {
  const { error } = await db
    .from('user_moves')
    .upsert(
      { user_id: userId, move_id: moveId },
      { onConflict: 'user_id,move_id', ignoreDuplicates: true },
    );
  if (error) throw error;
}

/** L'admin retire des Moves d'un compte : ceux donnés, ou tous (`moveIds` absent). */
export function useDeleteUserMoves(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (moveIds?: readonly string[]) => {
      let q = db.from('user_moves').delete().eq('user_id', userId);
      if (moveIds) q = q.in('move_id', [...moveIds]);
      const { error } = await q;
      if (error) throw error;
    },
    onSettled: () => qc.invalidateQueries({ queryKey: userMovesKey(userId) }),
  });
}
